import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { clientService } from '../services/api';

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  
  // Editable fields
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    notes: '',
    status: 'active',
  });

  useEffect(() => {
    loadClient();
  }, [id]);

  const loadClient = async () => {
    try {
      setLoading(true);
      const data = await clientService.getById(id);
      setClient(data);
      setFormData({
        name: data.name || '',
        company: data.company || '',
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || '',
        website: data.website || '',
        notes: data.notes || '',
        status: data.status || 'active',
      });
    } catch (err) {
      console.error('Error loading client:', err);
      setError('Failed to load client');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const updated = await clientService.update(id, formData);
      setClient({ ...client, ...updated });
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving client:', err);
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this client? This action cannot be undone.')) {
      return;
    }
    
    try {
      await clientService.delete(id);
      navigate('/clients');
    } catch (err) {
      console.error('Error deleting client:', err);
      setError('Failed to delete client');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge-success',
      inactive: 'badge-gray',
      prospect: 'badge-info',
    };
    return badges[status] || 'badge-gray';
  };

  const getProposalStatusBadge = (status) => {
    const badges = {
      draft: 'badge-gray',
      pending: 'badge-warning',
      accepted: 'badge-success',
      declined: 'badge-danger',
      expired: 'badge-gray',
    };
    return badges[status] || 'badge-gray';
  };

  const getEngagementStatusBadge = (status) => {
    const badges = {
      active: 'badge-success',
      completed: 'badge-info',
      paused: 'badge-warning',
      cancelled: 'badge-danger',
    };
    return badges[status] || 'badge-gray';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-500">Client not found</p>
        <Link to="/clients" className="text-accent hover:underline mt-4 inline-block">
          Back to Clients
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Client: {client.name} | CR Hood Admin</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/clients"
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {client.company || client.name}
              </h1>
              {client.company && (
                <p className="text-sm text-gray-500">{client.name}</p>
              )}
            </div>
            <span className={`badge ${getStatusBadge(client.status)}`}>
              {client.status}
            </span>
          </div>
          
          <div className="flex items-center space-x-3">
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn btn-primary"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn btn-secondary"
                >
                  Edit
                </button>
                <button
                  onClick={handleDelete}
                  className="btn btn-danger"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Information */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Contact Information</h2>
              
              {isEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Contact Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label">Company</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>
                  <div>
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>
                  <div>
                    <label className="form-label">Website</label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="https://"
                    />
                  </div>
                  <div>
                    <label className="form-label">Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="form-input"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="prospect">Prospect</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="form-label">Address</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      rows={2}
                      className="form-input"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-500">Contact Name</label>
                    <p className="font-medium">{client.name}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Company</label>
                    <p className="font-medium">
                      {client.company || <span className="text-gray-400">—</span>}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Email</label>
                    <p className="font-medium">
                      <a href={`mailto:${client.email}`} className="text-accent hover:underline">
                        {client.email}
                      </a>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Phone</label>
                    <p className="font-medium">
                      {client.phone ? (
                        <a href={`tel:${client.phone}`} className="text-accent hover:underline">
                          {client.phone}
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Website</label>
                    <p className="font-medium">
                      {client.website ? (
                        <a 
                          href={client.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-accent hover:underline"
                        >
                          {client.website}
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Address</label>
                    <p className="font-medium whitespace-pre-line">
                      {client.address || <span className="text-gray-400">—</span>}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Notes</h2>
              {isEditing ? (
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={5}
                  className="form-input"
                  placeholder="Add notes about this client..."
                />
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap text-gray-700 min-h-[100px]">
                  {client.notes || <span className="text-gray-400 italic">No notes</span>}
                </div>
              )}
            </div>

            {/* Proposals */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Proposals</h2>
                <Link 
                  to={`/proposals/new?client=${id}`}
                  className="btn btn-secondary text-sm"
                >
                  + New Proposal
                </Link>
              </div>
              {client.proposals && client.proposals.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {client.proposals.map((proposal) => (
                    <div key={proposal.id} className="py-3 flex items-center justify-between">
                      <div>
                        <Link 
                          to={`/proposals/${proposal.id}`}
                          className="font-medium text-gray-900 hover:text-accent"
                        >
                          {proposal.title}
                        </Link>
                        <div className="text-sm text-gray-500">
                          ${proposal.amount?.toLocaleString() || '0'} • {new Date(proposal.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <span className={`badge ${getProposalStatusBadge(proposal.status)}`}>
                        {proposal.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm py-4">No proposals yet</p>
              )}
            </div>

            {/* Engagements */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Engagements</h2>
                <Link 
                  to={`/engagements/new?client=${id}`}
                  className="btn btn-secondary text-sm"
                >
                  + New Engagement
                </Link>
              </div>
              {client.engagements && client.engagements.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {client.engagements.map((engagement) => (
                    <div key={engagement.id} className="py-3 flex items-center justify-between">
                      <div>
                        <Link 
                          to={`/engagements/${engagement.id}`}
                          className="font-medium text-gray-900 hover:text-accent"
                        >
                          {engagement.title}
                        </Link>
                        <div className="text-sm text-gray-500">
                          {new Date(engagement.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <span className={`badge ${getEngagementStatusBadge(engagement.status)}`}>
                        {engagement.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm py-4">No engagements yet</p>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
              <div className="space-y-2">
                <a
                  href={`mailto:${client.email}`}
                  className="btn btn-secondary w-full"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Send Email
                </a>
                {client.phone && (
                  <a
                    href={`tel:${client.phone}`}
                    className="btn btn-secondary w-full"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Call
                  </a>
                )}
                <Link
                  to={`/proposals/new?client=${id}`}
                  className="btn btn-primary w-full"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  New Proposal
                </Link>
              </div>
            </div>

            {/* Metadata */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Details</h2>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-gray-500">Client ID</dt>
                  <dd className="font-mono text-gray-900">#{client.id}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Status</dt>
                  <dd>
                    <span className={`badge ${getStatusBadge(client.status)}`}>
                      {client.status}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Created</dt>
                  <dd className="text-gray-900">{formatDate(client.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Last Updated</dt>
                  <dd className="text-gray-900">{formatDate(client.updatedAt)}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
